import appManager from '@app/appManager';
import AppModule from '@app/lib/AppModule';
import { GET_SECOND_CHANCE_LEADS_FRONTEND_SETTINGS } from './data/settings';
import { GET_CONTACT_CENTER_SETTINGS } from './data/contactCenterSettings';
import { useLazyQuery } from '@apollo/client/react';
import {
  Exact,
  SecondChanceLeadsFrontendSettingsQuery,
} from '@api/generated/graphql';
import AuthModule from '@modules/AuthModule';
import { getClient } from '@api/client';
import { frontEndSettingsState } from './state';
import { FrontEndSettingsState } from './types';
import updateWorkflowNameSetting from './mutation/updateWorkflowNameSetting';
import updateCallTimeoutWarningSetting from './mutation/updateCallTimeoutWarningSetting';
import { ApolloClient } from '@apollo/client';

let executeQuery: useLazyQuery.ExecFunction<
  SecondChanceLeadsFrontendSettingsQuery,
  Exact<{ contactCenterId: string }>
>;

let queryResults: ApolloClient.QueryResult<SecondChanceLeadsFrontendSettingsQuery>;

let isLoadingSclSettings = false;

/**
 * Initialize FrontEndSettingsModule's internal lazy queries.
 *
 * This must be called before the module is used.
 */
export function useFrontEndSettingsQuery() {
  // TODO: Find a way to not create and discard a lazy query object on every
  // call to useFrontEndSettings (which depends on this method).
  //
  // It's not a big deal - it'll get garbage-collected. It's just a needless
  // use of CPU cycles.
  //
  // Using '@apollo/client/core' to do a call without involving React could be an option:
  //
  // https://www.apollographql.com/docs/react/migrating/apollo-client-3-migration#using-apollo-client-without-react
  const [execute] = useLazyQuery(GET_SECOND_CHANCE_LEADS_FRONTEND_SETTINGS, {
    fetchPolicy: 'network-only',
  });

  if (!executeQuery) {
    executeQuery = execute;
  }
}

export enum EventNames {
  SecondChanceLeadsSettingsUpdate = 'frontendsettings.secondchanceleads.update',
}

/**
 * A module for loading and reading user-customizable CCP settings that change
 * the frontend's behavior, but are fetched from the BFF.
 *
 * At present this contains only one setting. Expect growing pains expanding it
 * to support more settings.
 *
 * TODO: Find a clearer name for this.
 */
export class FrontEndSettingsModule extends AppModule {
  override events = [EventNames.SecondChanceLeadsSettingsUpdate];

  override preload(): void {
    if (executeQuery === undefined) {
      this.app.logger.warn(
        'FrontEndSettingsModule: executeQuery is undefined, ensure useSecondChanceLeadsFrontendSettings() is called before this module is used.',
      );
    }

    this.app.events.on('featureflags.initialized', async () => {
      appManager.events.emit(EventNames.SecondChanceLeadsSettingsUpdate);

      this.fetchContactCenterSettings();
    });

    this.app.events.on(EventNames.SecondChanceLeadsSettingsUpdate, () => {
      this.app.logger.info('trigger SCL settings refresh');

      this.fetchSclSettings(true);
    });
  }

  /**
   * @return A promise to update settings from the BFF. By default, if settings
   * have already been fetched, they will not be refetched.
   *
   * @param force If true, refetch settings from the BFF even when they're
   * already populated. USE SPARINGLY.
   *
   * As of 2025-07, we've had more than one incident where CCP overwhelmed a
   * backend component due to repeatedly polling to detect updates.
   *
   * We don't want another one.
   *
   * TODO: Once the BFF supports websocket subscriptions, add support for
   * receiving settings updates pushed from the BFF. That should give us a
   * responsive UX without hammering things to death.
   */
  public async fetchSclSettings(force = false): Promise<void> {
    this.app.logger.info('Fetch SCL settings', force);

    if (!AuthModule.user) {
      this.app.logger.info('No user');
      throw new Error('Cannot fetch frontend settings before user exists');
    }

    if (isLoadingSclSettings) {
      // There's a fetch in progress - no need to fetch even if force is true.
      this.app.logger.info('Already loading SCL settings - do nothing');
      return;
    }

    if (!force && queryResults?.data) {
      // We already have data and it wasn't a force - no need to fetch again.
      return;
    }

    isLoadingSclSettings = true;

    try {
      queryResults = await executeQuery?.({
        variables: {
          contactCenterId: AuthModule.user?.contactCenterId,
        },
      });

      if (queryResults?.data?.sclSettingsFrontEnd?.daysUntilCold) {
        frontEndSettingsState.followUps.secondChanceLeads.daysUntilCold =
          queryResults.data?.sclSettingsFrontEnd.daysUntilCold;
      }
    } catch (error) {
      // Apollo v4's default `errorPolicy: 'none'` rejects the execute promise
      // on GraphQL/network errors (v3 resolved with `.error` on the result).
      // Log and swallow so the event-handler caller doesn't see an unhandled
      // rejection — matches the sibling `fetchContactCenterSettings` pattern.
      this.app.logger.error('Error fetching SCL settings', error);
    } finally {
      // Even if there's an error on fetch, we still want to mark it as ready.
      //
      // We reached the backend, and if load wasn't successful, we'll use the
      // hardcoded fallback value.
      frontEndSettingsState.followUps.secondChanceLeads.isReady = true;
      isLoadingSclSettings = false;
    }
  }

  /**
   * Fetches contact center settings from the BFF.
   */
  public async fetchContactCenterSettings(): Promise<void> {
    this.app.logger.info('Fetch contact center settings');

    if (!AuthModule.user) {
      this.app.logger.info('No user');
      throw new Error(
        'Cannot fetch contact center settings before user exists',
      );
    }

    // Even if there's an error on fetch, we still want to mark it as ready.
    // We reached the backend, and if load wasn't successful, we'll use the
    // default value.
    try {
      const result = await getClient().query({
        query: GET_CONTACT_CENTER_SETTINGS,
        fetchPolicy: 'network-only',
      });

      frontEndSettingsState.contactCenterSettings.showWorkflowNameOnInboundCalls.isEnabled =
        result.data?.contactCenterSettings.showWorkflowNameOnInboundCalls
          ?.isEnabled ?? false;

      frontEndSettingsState.contactCenterSettings.callTimeoutWarning.isEnabled =
        result.data?.contactCenterSettings.callTimeoutWarning?.isEnabled ??
        false;
    } catch (error) {
      this.app.logger.error('Error fetching contact center settings', error);
    } finally {
      frontEndSettingsState.contactCenterSettings.showWorkflowNameOnInboundCalls.isReady =
        true;
      frontEndSettingsState.contactCenterSettings.callTimeoutWarning.isReady =
        true;
    }
  }

  /**
   * Updates the showWorkflowNameOnInboundCalls setting for the contact center.
   *
   * @param isEnabled - Whether to enable or disable showing workflow name on
   * inbound calls.
   * @returns {Promise<boolean>} A promise that resolves to true if the update
   *                             was successful, false otherwise.
   */
  public async updateShowWorkflowNameOnInboundCalls(
    isEnabled: boolean,
  ): Promise<boolean> {
    if (!AuthModule.user?.contactCenterId) {
      this.app.logger.error(
        'No contactCenterId available for updating setting',
      );
      return false;
    }

    try {
      const newValue = await updateWorkflowNameSetting(isEnabled);

      frontEndSettingsState.contactCenterSettings.showWorkflowNameOnInboundCalls.isEnabled =
        newValue;

      return true;
    } catch (error) {
      this.app.logger.error('Error updating workflow name setting', error);
      return false;
    }
  }

  /**
   * Updates the callTimeoutWarning setting for the contact center.
   *
   * @param isEnabled - Whether to enable or disable call timeout warnings.
   * @returns {Promise<boolean>} A promise that resolves to true if the update
   *                             was successful, false otherwise.
   */
  public async updateCallTimeoutWarning(isEnabled: boolean): Promise<boolean> {
    if (!AuthModule.user?.contactCenterId) {
      this.app.logger.error(
        'No contactCenterId available for updating setting',
      );
      return false;
    }

    try {
      const newValue = await updateCallTimeoutWarningSetting(isEnabled);

      frontEndSettingsState.contactCenterSettings.callTimeoutWarning.isEnabled =
        newValue;

      return true;
    } catch (error) {
      this.app.logger.error(
        'Error updating call timeout warning setting',
        error,
      );
      return false;
    }
  }

  /**
   * Checks if a contact center setting is enabled.
   *
   * @param settingKey - The key of the contact center setting to check.
   * @returns {boolean} True if the setting is enabled, false otherwise.
   */
  public isContactCenterSettingsEnabled(
    settingKey: keyof FrontEndSettingsState['contactCenterSettings'],
  ): boolean {
    const setting = frontEndSettingsState.contactCenterSettings[settingKey];
    return setting?.isEnabled ?? false;
  }
}

export default new FrontEndSettingsModule(
  appManager.events,
  appManager.logger('FrontEndSettings'),
);
